import keycloakInstance from "@/initKeycloak";
import { TabStore } from "@/stores/useTabStore";
import axios from "axios";
import { debounce } from "lodash";
import * as Sentry from "@sentry/react";
import { attachRetryOn401 } from "@/shared/api/interceptors/retryOn401";

export const baseURL = import.meta.env.VITE_PREF_STORAGE_BASE_URL;

export const endpoints = {
  getUserTabs: "/preferences/user/tabs",
  patchUserTabs: "/preferences/user/tabs",
  getSubscription: "/subscription",
  postDiagnosticReport: "/diagnostics-reports",
};
export const client = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

client.interceptors.request.use(async (config) => {
  if (keycloakInstance && keycloakInstance.authenticated) {
    try {
      await keycloakInstance.updateToken(30); // Refresh if expiring within 30s
    } catch (error) {
      // Transient refresh failures (network blip to Keycloak, iframe race, etc.)
      // shouldn't drop the existing token — fall through and send what we have.
      // The 401/403 retry interceptor handles the case where the existing token
      // is actually invalid.
      console.error("Token refresh error in interceptor:", error);
    }
    config.headers.Authorization = `Bearer ${keycloakInstance.token}`;
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("[pref-storage] API error:", {
      message: error.message,
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
    });
    // Skip Sentry for expected 404 on /subscription (no subscription exists yet)
    const isSubscription404 =
      error.response?.status === 404 &&
      error.config?.url?.includes(endpoints.getSubscription);

    if (!isSubscription404) {
      Sentry.captureException(error, {
        extra: {
          axiosDetails: {
            msg: error.message,
            code: error.code,
            config: error.config,
            request: error.request,
            response: error.response,
            data: error.response?.data,
          },
        },
      });
    }
    return Promise.reject(error);
  }
);

attachRetryOn401(client);

export const getUserTabs = async () => {
  return (await client.get(endpoints.getUserTabs)).data?.value;
};

export const saveUserTabs = async (tabs: TabStore["tabs"]) => {
  return (await client.patch(endpoints.patchUserTabs, { value: tabs })).data;
};

export const saveUserTabsDebounced = debounce(saveUserTabs, 3 * 1000, {
  leading: true,
});

export const getSubscription = async () => {
  return (await client.get(endpoints.getSubscription)).data;
};

export const submitDiagnosticReport = async (report: unknown) => {
  return (await client.post(endpoints.postDiagnosticReport, { payload: report }))
    .data;
};

// Re-export tags watchlists module
export * from "./tags-watchlists";
export * from "./tags-watchlists/useTagsWatchlistsCRUD";
